import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  DEFAULT_CONSENT,
  getStoredConsent,
  saveConsent,
} from "../utils/cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) setConsent(stored);
    else setBannerVisible(true);
  }, []);

  const applyAndPersist = useCallback((next) => {
    const saved = saveConsent(next);
    setConsent(saved);
    setBannerVisible(false);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => applyAndPersist({ necessary: true, analytics: true, marketing: true }),
    [applyAndPersist]
  );

  const rejectAll = useCallback(
    () =>
      applyAndPersist({ necessary: true, analytics: false, marketing: false }),
    [applyAndPersist]
  );

  const savePreferences = useCallback(
    (partial) => applyAndPersist({ necessary: true, ...partial }),
    [applyAndPersist]
  );

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        bannerVisible,
        preferencesOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferences,
        closePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider"
    );
  }
  return ctx;
}
